// hooks/useSupportNotifications.js
// ─────────────────────────────────────────────────────────────
// Global notification listener — plays a sound AND tracks unread
// state when a support message arrives while the chat screen is not
// open. Must live outside the chat component entirely: by the time
// the chat is closed, the chat component is unmounted and can't hear
// anything. Mounted ONCE, high up the tree (root layout / App.jsx),
// independent of whether any support UI is currently visible.
//
// FIXED — added UNREAD TRACKING, previously this hook only played a
// sound and had no persistent "has the user seen this" state at all.
// FloatingSupportButton (rendered on arbitrary OTHER pages, not
// necessarily anywhere near the chat) needs to know whether there's
// an unread message to show a badge — that's state that has to
// survive across page navigation and outlive any single component's
// lifetime, which is exactly why this lives here as module-level
// state with a subscriber pattern, the same shape chatIsOpen already
// used, rather than as React state owned by any one component.
//
// "Chat screen open" (chatIsOpen) is unchanged — flipped by
// SupportChat.jsx on mount/unmount. Opening the chat now ALSO clears
// unread state (see setSupportChatOpen below), since the natural
// "mark as read" moment is exactly when the user opens the chat —
// no separate "mark read" call needed anywhere else.
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { useSocket } from "./useSocket";

let chatIsOpen = false;

// FIXED — new module-level unread state + a tiny subscriber list.
// Plain module state (not React context) so components with no
// parent/child relationship to each other — SupportChat and
// FloatingSupportButton may sit on completely different routes — can
// both read and react to the same value.
let unreadCount = 0;
const subscribers = new Set();

function notifySubscribers() {
  subscribers.forEach(fn => fn(unreadCount));
}

export function setSupportChatOpen(open) {
  chatIsOpen = open;
  if (open) {
    // FIXED — opening the chat is the natural "mark as read" moment.
    // No separate markAsRead() call needed elsewhere; every existing
    // call site of setSupportChatOpen(true) (just SupportChat.jsx's
    // mount effect) gets this for free.
    unreadCount = 0;
    notifySubscribers();
  }
}

// FIXED — exported so FloatingSupportButton (or anything else that
// wants a live unread badge) can subscribe without needing to be
// related to useSupportNotifications' own mount point in the tree.
// Returns the current count immediately AND keeps it updated;
// `useUnreadSupportCount` below wraps this into a hook for the common
// case of "just give me a number that updates."
export function subscribeToUnreadCount(fn) {
  subscribers.add(fn);
  fn(unreadCount); // immediate current value on subscribe
  return () => subscribers.delete(fn);
}

export function useUnreadSupportCount() {
  const [count, setCount] = useState(unreadCount);
  useEffect(() => subscribeToUnreadCount(setCount), []);
  return count;
}

export function useSupportNotifications(userId) {
  const { on, off } = useSocket(userId);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/sounds/support-notification.mp3");
      audioRef.current.volume = 0.6;
    }

    const handleNotification = () => {
      if (chatIsOpen) return; // visible in-thread already — no sound, no badge bump needed

      // FIXED — bump the unread count (was previously just a sound
      // with no persisted state at all).
      unreadCount += 1;
      notifySubscribers();

      audioRef.current?.play().catch(() => {
        // Autoplay blocked — acceptable, not worth surfacing to the user.
      });
    };

    on("newMessageNotification", handleNotification);
    return () => off("newMessageNotification", handleNotification);
  }, [userId, on, off]);
}